export const downloadHelper = (fileName: string, parts: BlobPart[]) => {
  const blob = new Blob(parts);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName || 'video.mp4';
  a.click();
  URL.revokeObjectURL(a.href);
};

export const checkOnlineHelper = async (serverUrl: string, headers: HeadersInit) => {
  let hasInternetConnection = false;

  if (typeof navigator !== 'undefined' && navigator.onLine) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${serverUrl}/info/version`, {
        method: 'GET',
        headers: headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      hasInternetConnection = response.ok || response.status === 204;
    } catch (e) {
      hasInternetConnection = false;
    }
  }

  if (hasInternetConnection) return;

  await new Promise<void>((res) => window.addEventListener('online', () => res(), { once: true }));
  await new Promise((r) => setTimeout(r, 300));
};

export const chunkDownloadHelper = async (
  serverUrl: string,
  url: string,
  fileSize: number,
  headers: HeadersInit,
  onError: (status: number, message: string, body: Record<string, any>) => void,
  onProgress?: (loaded: number, total: number) => void
): Promise<{
  parts: BlobPart[] | null;
  fileType: string | null;
  downloadFailed: boolean;
}> => {
  const total = fileSize;
  let offset = 0;
  const parts: BlobPart[] = [];
  const maxRetries = 2;
  let downloadFailed = false;
  let retries = 0;
  let fileType;

  while (offset < total && !downloadFailed) {
    headers['Range'] = `bytes=${offset}-${total - 1}`;
    const res = await fetch(url, {
      headers: headers,
    });
    try {
      if (!res.ok) throw new Error();
      fileType = res?.headers.get('Content-Type');

      const reader = res.body!.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value!);
        offset += value!.length;
        onProgress?.(offset, total);
      }
    } catch (e: any) {
      const looksOffline =
        (e instanceof TypeError && e.message === 'Failed to fetch') || res?.status === 0;

      if (looksOffline) {
        await checkOnlineHelper(serverUrl, headers);
        continue;
      }

      retries++;
      if (retries > maxRetries) {
        onError(res.status, res.statusText, {});
        downloadFailed = true;
        break;
      }
    }
  }
  if (downloadFailed) {
    return { parts: null, fileType: null, downloadFailed };
  }
  return {
    parts,
    fileType,
    downloadFailed,
  };
};
